import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, TrendingUp, CheckCircle2, XCircle, Clock, AlertCircle, HelpCircle, Download, Smartphone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { programsAPI, athletesAPI, attendanceAPI } from '../services/api';
import Tutorial from '../components/Tutorial';
import SubscriptionLockedModal from '../components/SubscriptionLockedModal';
import useSubscription from '../hooks/useSubscription';

const Dashboard = () => {
  const [programs, setPrograms] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const [stats, setStats] = useState({
    totalPrograms: 0,
    totalAthletes: 0,
    todayPresent: 0,
    todayAbsent: 0,
    todayLate: 0,
    attendanceRate: 0
  });
  
  const { isLocked, daysUntilExpiry, isExpiringSoon, isPro } = useSubscription();

  useEffect(() => {
    fetchData();
    
    // Check if tutorial should auto-show
    const tutorialCompleted = localStorage.getItem('tutorialCompleted');
    if (!tutorialCompleted) {
      setTimeout(() => {
        setIsTutorialOpen(true);
      }, 1000);
    }

    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsAppInstalled(true);
    }

    // Listen for the install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    // Listen for successful install
    const handleAppInstalled = () => {
      setIsAppInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (installPrompt) {
      // Chrome / Edge / Android: use native install prompt
      installPrompt.prompt();
      const result = await installPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setIsAppInstalled(true);
      }
      setInstallPrompt(null);
    } else if (isIOS) {
      // iOS Safari: show instructions modal
      setShowIOSInstructions(true);
    }
  };

  // Only show install button when install is actually possible
  const canShowInstall = !isAppInstalled && (installPrompt || isIOS);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [programsRes, athletesRes, attendanceRes] = await Promise.all([
        programsAPI.getAll(),
        athletesAPI.getAll(),
        attendanceAPI.getAll()
      ]);
      
      const loadedPrograms = programsRes.data;
      const loadedAthletes = athletesRes.data;
      const loadedAttendance = attendanceRes.data;
      
      setPrograms(loadedPrograms);
      setAthletes(loadedAthletes);
      setAttendance(loadedAttendance);

      // Calculate stats
      const today = new Date().toISOString().split('T')[0];
      const todayAttendance = loadedAttendance.filter(a => a.date === today);
      
      const present = todayAttendance.filter(a => a.status === 'present').length;
      const absent = todayAttendance.filter(a => a.status === 'absent').length;
      const late = todayAttendance.filter(a => a.status === 'late').length;
      const total = todayAttendance.length;

      // Only count athletes who belong to at least one active (non-archived) program
      const activeProgramIds = new Set(loadedPrograms.map(p => p.id));
      const activeAthletes = loadedAthletes.filter(a =>
        a.programs?.some(pid => activeProgramIds.has(pid))
      );
      
      setStats({
        totalPrograms: loadedPrograms.length,
        totalAthletes: activeAthletes.length,
        todayPresent: present,
        todayAbsent: absent,
        todayLate: late,
        attendanceRate: total > 0 ? Math.round(((present + late) / total) * 100) : 0
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card className="border-l-4 transition-all hover:shadow-lg" style={{ borderLeftColor: color }}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-gray-600">{title}</CardTitle>
        <Icon className="h-5 w-5" style={{ color }} />
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold" style={{ color }}>{value}</div>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Subscription Locked Modal */}
      <SubscriptionLockedModal isOpen={isLocked} />

      {/* iOS Install Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowIOSInstructions(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Install on your device</h3>
            <div className="space-y-4 py-2">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-700">1</div>
                <p className="text-sm text-gray-700 pt-0.5">
                  Tap the <strong>Share</strong> button <span className="inline-block px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">⬆</span> at the bottom of Safari
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-700">2</div>
                <p className="text-sm text-gray-700 pt-0.5">
                  Scroll down and tap <strong>"Add to Home Screen"</strong>
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-700">3</div>
                <p className="text-sm text-gray-700 pt-0.5">
                  Tap <strong>"Add"</strong> — the app will appear on your home screen
                </p>
              </div>
            </div>
            <Button onClick={() => setShowIOSInstructions(false)} className="w-full mt-4" style={{ backgroundColor: '#0067B9' }}>
              Got it
            </Button>
          </div>
        </div>
      )}

      {/* Expiry Warning Banner */}
      {isExpiringSoon && !isLocked && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-amber-500 mr-3" />
              <div>
                <p className="font-medium text-amber-800">
                  Your Pro subscription expires in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}
                </p>
                <p className="text-sm text-amber-600">
                  Renew now to keep unlimited access to all features.
                </p>
              </div>
            </div>
            <Link to="/subscription">
              <Button size="sm" style={{ backgroundColor: '#007A3D' }}>
                Renew Now
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Tutorial */}
      <Tutorial 
        isOpen={isTutorialOpen} 
        onClose={() => setIsTutorialOpen(false)}
        autoShow={!localStorage.getItem('tutorialCompleted')}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back, Coach</p>
        </div>
        <div className="flex items-center gap-2">
          {canShowInstall && (
            <Button
              onClick={handleInstallClick}
              style={{ backgroundColor: '#0067B9' }}
              className="hover:opacity-90 flex items-center space-x-2"
              data-testid="install-app-btn"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Install App</span>
              <span className="sm:hidden">Install</span>
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setIsTutorialOpen(true)}
            className="flex items-center space-x-2"
          >
            <HelpCircle className="h-4 w-4" />
            <span>Tutorial</span>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Programs"
          value={stats.totalPrograms}
          icon={Calendar}
          color="#0067B9"
        />
        <StatCard
          title="Total Athletes"
          value={stats.totalAthletes}
          icon={Users}
          color="#007A3D"
        />
        <StatCard
          title="Today's Present"
          value={stats.todayPresent}
          icon={CheckCircle2}
          color="#007A3D"
        />
        <StatCard
          title="Attendance Rate"
          value={`${stats.attendanceRate}%`}
          icon={TrendingUp}
          color="#0067B9"
        />
      </div>

      {/* Today's Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Today's Attendance Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.todayPresent}</p>
                <p className="text-sm text-green-600">Present</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-4 bg-yellow-50 rounded-lg">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold text-yellow-700">{stats.todayLate}</p>
                <p className="text-sm text-yellow-600">Late</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-4 bg-red-50 rounded-lg">
              <XCircle className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-700">{stats.todayAbsent}</p>
                <p className="text-sm text-red-600">Absent</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Programs Overview */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl">Active Programs</CardTitle>
          <Link to="/programs">
            <Button style={{ backgroundColor: '#0067B9' }} className="hover:opacity-90">
              View All Programs
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {programs.slice(0, 3).map(program => (
              <div
                key={program.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:shadow-md transition-shadow"
              >
                <div className="flex items-center space-x-4">
                  <div
                    className="w-3 h-12 rounded-full"
                    style={{ backgroundColor: program.color }}
                  />
                  <div>
                    <h3 className="font-semibold text-gray-800">{program.name}</h3>
                    <p className="text-sm text-gray-600">{program.schedule} • {program.time}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-800">{program.athleteCount} Athletes</p>
                  <p className="text-sm text-gray-600">{program.sport}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {canShowInstall && (
          <Card
            className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white md:col-span-3"
            onClick={handleInstallClick}
            data-testid="install-app-card"
          >
            <CardContent className="pt-6 pb-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Smartphone className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Install Attendance Tracker</h3>
                    <p className="text-sm text-gray-500">Add to your home screen for quick access — works like a native app</p>
                  </div>
                </div>
                <Button
                  style={{ backgroundColor: '#0067B9' }}
                  className="hover:opacity-90 shrink-0"
                  data-testid="install-app-card-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Install
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        <Link to="/attendance" className="block">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-blue-200">
            <CardContent className="pt-6">
              <div className="text-center space-y-3">
                <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-800">Take Attendance</h3>
                <p className="text-sm text-gray-600">Mark today's attendance</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/athletes" className="block">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-green-200">
            <CardContent className="pt-6">
              <div className="text-center space-y-3">
                <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-800">Manage Athletes</h3>
                <p className="text-sm text-gray-600">View and edit athlete roster</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/reports" className="block">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-red-200">
            <CardContent className="pt-6">
              <div className="text-center space-y-3">
                <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="font-semibold text-gray-800">View Reports</h3>
                <p className="text-sm text-gray-600">Analytics and insights</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;